/**
 * Full Deployed Theme Contract — single source of truth (Tailwind v4 CSS-first)
 * ===========================================================================
 *
 * This module holds the COMPLETE per-state token contract for all themes.
 * Color values are authored as `oklch()` strings (#1169), each converted
 * losslessly from the EXACT deployed sRGB hex — every value round-trips back
 * to its original hex (guard: `scripts/check-tokens-oklch.ts` vs the frozen
 * `theme-tokens-raw.json` baseline), so rendering is pixel-identical and the
 * #1170 WCAG-AA ratios are preserved. It is the machine-readable backfill of
 * `libs/ui/brand-kit/styles/theme.css` — preserved 1:1 from the deployed contract.
 *
 * The narrower `ThemeColors`/`themes` export in `./definitions` covers only
 * the 11 "core" fields the brand-showcase app consumes. THIS module is the
 * full ~30-var contract (bg, text, muted variants, ring, input, destructive,
 * success, warning, tints, links, …) that `generateThemeCSS()` emits.
 *
 * Going forward: edit the values HERE (or in `definitions.ts`, which is kept
 * in sync with these), then run `generateThemeCSS()` to regenerate theme.css.
 * A drift check (compare design-tokens vs theme.css) guards the link.
 *
 * #3071 S2: the LIGHT brand tokens of canvas, storm, blue, emerald, sunshine and magenta
 * (primary, accent, their foregrounds, primaryTint, neutralTint, ring, storm link) now come from
 * the brand-kit light combinations (libs/ui/brand-kit/themes/src/combinations.ts swatches),
 * per the owner ruling of 2026-09-27 on #3071. A foreground that fails 4.5:1 is swapped
 * (white, then #121212, then #000000), never the hue. The shared light chrome and every dark
 * state are unchanged.
 *
 * Every value below is the oklch() form of the deployed sRGB (snapshotted in
 * `theme-tokens-raw.json`). DO NOT "correct" these to brand-data palette values
 * — the DEPLOYED values are canonical (Ben's decision, 2026-06-21). To change a
 * color, edit the oklch() here and regenerate; the round-trip guard will fail if
 * a value no longer matches the baseline unless the baseline is updated too.
 */

/** The full set of runtime CSS custom properties a theme state can set. */
export interface ThemeStateTokens {
  // Surfaces
  bg: string;
  background: string;
  surface: string;
  surfaceAlt: string;
  // Text
  text: string;
  foreground: string;
  textMuted: string;
  heading: string;
  subheading: string;
  // Brand
  primary: string;
  primaryForeground: string;
  /** storm/blue/emerald/sunshine only */
  primaryTint?: string;
  accent: string;
  accentForeground: string;
  /** storm/blue/emerald/sunshine only */
  neutralTint?: string;
  // Form / focus
  border: string;
  input: string;
  ring: string;
  muted: string;
  mutedForeground: string;
  // Links — storm + midnight-blueprint only
  link?: string;
  linkHover?: string;
  // Status
  destructive: string;
  destructiveForeground: string;
  success: string;
  successForeground: string;
  warning: string;
  warningForeground: string;
}

/**
 * Map of ThemeStateTokens key -> CSS custom property name.
 * Order here defines emit order inside each runtime block (matches theme.css).
 * The 6 alias tokens (--card, --popover, --secondary + -foreground) are NOT
 * here — they are emitted as a fixed trailer (they reference other vars).
 */
export const STATE_TOKEN_CSS_VARS: Array<[keyof ThemeStateTokens, string]> = [
  ['bg', '--bg'],
  ['background', '--background'],
  ['surface', '--surface'],
  ['surfaceAlt', '--surface-alt'],
  ['text', '--text'],
  ['foreground', '--foreground'],
  ['textMuted', '--text-muted'],
  ['heading', '--heading'],
  ['subheading', '--subheading'],
  ['primary', '--primary'],
  ['primaryForeground', '--primary-foreground'],
  ['primaryTint', '--primary-tint'],
  ['accent', '--accent'],
  ['accentForeground', '--accent-foreground'],
  ['neutralTint', '--neutral-tint'],
  ['border', '--border'],
  ['input', '--input'],
  ['ring', '--ring'],
  ['muted', '--muted'],
  ['mutedForeground', '--muted-foreground'],
  ['link', '--link'],
  ['linkHover', '--link-hover'],
  ['destructive', '--destructive'],
  ['destructiveForeground', '--destructive-foreground'],
  ['success', '--success'],
  ['successForeground', '--success-foreground'],
  ['warning', '--warning'],
  ['warningForeground', '--warning-foreground'],
];

/**
 * The 6 alias tokens emitted at the end of every full runtime block.
 * They reference other runtime vars (hence `@theme inline` at the names side).
 */
export const ALIAS_TOKEN_LINES: Array<[string, string]> = [
  ['--card', 'var(--surface)'],
  ['--card-foreground', 'var(--text)'],
  ['--popover', 'var(--surface)'],
  ['--popover-foreground', 'var(--text)'],
  ['--secondary', 'var(--surface-alt)'],
  ['--secondary-foreground', 'var(--text)'],
];

// ───────────────────────────────────────────────────────────────────────────
// Shared light "neutral chrome" — canvas/storm/blue/emerald/sunshine/magenta
// all share the same light surfaces/text/border/muted/status. Only the brand
// trio (primary/accent + their fg + tints/links/ring) differs per variant.
// Captured verbatim from theme.css.
// ───────────────────────────────────────────────────────────────────────────
const LIGHT_NEUTRAL = {
  bg: 'oklch(1 0 0)',
  background: 'oklch(1 0 0)',
  surface: 'oklch(1 0 0)',
  surfaceAlt: 'oklch(0.9791 0 0)',
  text: 'oklch(0.1822 0 0)',
  foreground: 'oklch(0.1822 0 0)',
  textMuted: 'oklch(0.4276 0 0)',
  heading: 'oklch(0.1822 0 0)',
  subheading: 'oklch(0.2645 0 0)',
  // #1170 Decision 2 (form-field-only scope, owner sign-off): light --border
  // is DECORATIVE (separators/dividers via bg-border, checkbox bare `border`)
  // and is WCAG 1.4.11-EXEMPT, so it is REVERTED to its original #e4e4e4. The
  // WCAG-passing value moves to the form-field-outline token --input below.
  border: 'oklch(0.9189 0 0)',
  // #1170 Decision 2: --input is the input/select/textarea/OTP outline color
  // (border-input -> border-color: var(--input)). It was `transparent` (no
  // visible outline, 0:1) -> #8e8e8e (3.28:1 vs #fff) to meet WCAG 1.4.11 for
  // interactive form-field outlines. (--input is NOT used as a light-mode fill;
  // the dark-only bg-input/30 fill is unaffected.)
  input: 'oklch(0.6467 0 0)',
  muted: 'oklch(0.958 0.0083 91.48)',
  mutedForeground: 'oklch(0.4276 0 0)',
  destructive: 'oklch(0.6316 0.2237 11.89)',
  // #1170 SIGN-OFF (Decision 1, fg-swap AA): keep brand hue #f13265; swap fg
  // #ffffff (3.90:1 — fails AA) -> #121212 (4.80:1 vs #f13265 — passes AA 4.5).
  destructiveForeground: 'oklch(0.1822 0 0)',
  success: 'oklch(0.8211 0.1969 154.06)',
  successForeground: 'oklch(0.1822 0 0)',
  warning: 'oklch(0.8989 0.1648 95.59)',
  warningForeground: 'oklch(0.1822 0 0)',
} as const;

/**
 * Full token map per theme state. Keys: `<variant>` (light) and
 * `<variant>.dark`. midnight-blueprint is standalone (no light/dark split in
 * the same sense; its single block doubles as storm.dark — see SELECTORS).
 * Every value is the DEPLOYED hex.
 */
export const themeStates: Record<string, ThemeStateTokens> = {
  // ── CANVAS ────────────────────────────────────────────────────────────────
  'canvas': {
    ...LIGHT_NEUTRAL,
    primary: 'oklch(0.5644 0.2014 259.39)',
    primaryForeground: 'oklch(1 0 0)',
    accent: 'oklch(0.6007 0.2323 348.1)',
    accentForeground: 'oklch(0 0 0)',
    ring: 'oklch(0.5644 0.2014 259.39)',
  },
  'canvas.dark': {
    bg: 'oklch(0.2178 0 0)',
    background: 'oklch(0.2178 0 0)',
    surface: 'oklch(0.2686 0 0)',
    surfaceAlt: 'oklch(0.3092 0 0)',
    text: 'oklch(0.9851 0 0)',
    foreground: 'oklch(0.9851 0 0)',
    textMuted: 'oklch(0.7668 0 0)',
    heading: 'oklch(1 0 0)',
    subheading: 'oklch(0.9851 0 0)',
    primary: 'oklch(0.6437 0.177 260.68)',
    // #1170 Decision 1 (fg-swap AA): keep blue #4a89f7; fg #ffffff (3.38:1 —
    // fails) -> canvas.dark bg #1a1a1a (5.15:1 — passes AA).
    primaryForeground: 'oklch(0.2178 0 0)',
    accent: 'oklch(0.693 0.1873 11.98)',
    // #1170 Decision 1 (fg-swap AA): keep #f9607d; fg #ffffff (3.00:1 — fails)
    // -> #1a1028 (6.09:1 — passes AA).
    accentForeground: 'oklch(0.2 0.048 301.44)',
    // #1170 Decision 3 (muted-nudge): dark border lifted #737373 -> #7d7d7d to
    // clear 3:1 vs --muted #303030 (3.21:1) while staying >=3:1 vs bg #1a1a1a
    // (4.23:1) and surface #262626 (3.68:1). Was #404040 pre-#1170.
    border: 'oklch(0.5897 0 0)',
    input: 'oklch(0.2686 0 0)',
    ring: 'oklch(0.6437 0.177 260.68)',
    muted: 'oklch(0.3092 0 0)',
    mutedForeground: 'oklch(0.7668 0 0)',
    destructive: 'oklch(0.6316 0.2237 11.89)',
    // #1170 Decision 1 (fg-swap AA): keep #f13265, fg #ffffff -> #121212 (4.80:1).
    destructiveForeground: 'oklch(0.1822 0 0)',
    success: 'oklch(0.8211 0.1969 154.06)',
    successForeground: 'oklch(0.2178 0 0)',
    warning: 'oklch(0.8989 0.1648 95.59)',
    warningForeground: 'oklch(0.2178 0 0)',
  },

  // ── STORM (dark block is shared with midnight-blueprint) ───────────────────
  'storm': {
    ...LIGHT_NEUTRAL,
    primary: 'oklch(0.2939 0.1395 262.33)',
    primaryForeground: 'oklch(1 0 0)',
    primaryTint: 'oklch(0.9128 0.0417 267.68)',
    accent: 'oklch(0.6007 0.2323 348.1)',
    accentForeground: 'oklch(0 0 0)',
    neutralTint: 'oklch(0.9789 0.0099 267.36)',
    ring: 'oklch(0.2939 0.1395 262.33)',
    link: 'oklch(0.2939 0.1395 262.33)',
    linkHover: 'oklch(0.3308 0.0836 261.44)',
  },
  'storm.dark': {
    bg: 'oklch(0.1566 0.0438 266.78)',
    background: 'oklch(0.1566 0.0438 266.78)',
    surface: 'oklch(0.2467 0.053 262.99)',
    surfaceAlt: 'oklch(0.2868 0.0577 261.53)',
    text: 'oklch(0.9253 0.0144 254.61)',
    foreground: 'oklch(0.9253 0.0144 254.61)',
    textMuted: 'oklch(0.7372 0.0343 258.37)',
    heading: 'oklch(1 0 0)',
    subheading: 'oklch(0.9253 0.0144 254.61)',
    primary: 'oklch(0.6437 0.177 260.68)',
    // #1170 Decision 1 (fg-swap AA): keep blue #4a89f7; fg #ffffff (3.38:1 —
    // fails) -> storm.dark bg #050b1f (5.78:1 — passes AA).
    primaryForeground: 'oklch(0.1566 0.0438 266.78)',
    accent: 'oklch(0.8989 0.1648 95.59)',
    accentForeground: 'oklch(0.1566 0.0438 266.78)',
    // #1170 Decision 3 (muted-nudge): dark border lifted #516ea0 -> #5d79a8 to
    // clear 3:1 vs --muted #1a2a47 (3.25:1) while staying >=3:1 vs bg #050b1f
    // (4.44:1) and surface #13203a (3.68:1). Was #2a3a57 pre-#1170.
    border: 'oklch(0.5739 0.08 260.49)',
    input: 'oklch(0.2467 0.053 262.99)',
    ring: 'oklch(0.6437 0.177 260.68)',
    muted: 'oklch(0.2868 0.0577 261.53)',
    mutedForeground: 'oklch(0.7372 0.0343 258.37)',
    link: 'oklch(0.6437 0.177 260.68)',
    linkHover: 'oklch(0.7141 0.1396 258.71)',
    destructive: 'oklch(0.6316 0.2237 11.89)',
    // #1170 Decision 1 (fg-swap AA): keep #f13265, fg #ffffff -> #121212 (4.80:1).
    destructiveForeground: 'oklch(0.1822 0 0)',
    success: 'oklch(0.8211 0.1969 154.06)',
    successForeground: 'oklch(0.1566 0.0438 266.78)',
    warning: 'oklch(0.8989 0.1648 95.59)',
    warningForeground: 'oklch(0.1566 0.0438 266.78)',
  },

  // ── BLUE ────────────────────────────────────────────────────────────────
  'blue': {
    ...LIGHT_NEUTRAL,
    primary: 'oklch(0.5072 0.1845 259.43)',
    primaryForeground: 'oklch(1 0 0)',
    primaryTint: 'oklch(0.9243 0.0306 258.94)',
    accent: 'oklch(0.8422 0.1708 85.58)',
    accentForeground: 'oklch(0.1822 0 0)',
    neutralTint: 'oklch(0.9813 0.0074 260.73)',
    ring: 'oklch(0.5072 0.1845 259.43)',
  },
  'blue.dark': {
    bg: 'oklch(0.1566 0.0438 266.78)',
    background: 'oklch(0.1566 0.0438 266.78)',
    surface: 'oklch(0.2467 0.053 262.99)',
    surfaceAlt: 'oklch(0.2868 0.0577 261.53)',
    text: 'oklch(0.9253 0.0144 254.61)',
    foreground: 'oklch(0.9253 0.0144 254.61)',
    textMuted: 'oklch(0.7372 0.0343 258.37)',
    heading: 'oklch(1 0 0)',
    subheading: 'oklch(0.9253 0.0144 254.61)',
    primary: 'oklch(0.5551 0.2211 260.57)',
    primaryForeground: 'oklch(1 0 0)',
    accent: 'oklch(0.9167 0.1378 95.84)',
    accentForeground: 'oklch(0.1566 0.0438 266.78)',
    // #1170 Decision 3 (muted-nudge): dark border lifted #516ea0 -> #5d79a8 to
    // clear 3:1 vs --muted #1a2a47 (3.25:1) while staying >=3:1 vs bg #050b1f
    // (4.44:1) and surface #13203a (3.68:1). Shares navy chrome with storm.dark.
    border: 'oklch(0.5739 0.08 260.49)',
    input: 'oklch(0.2467 0.053 262.99)',
    ring: 'oklch(0.5551 0.2211 260.57)',
    muted: 'oklch(0.2868 0.0577 261.53)',
    mutedForeground: 'oklch(0.7372 0.0343 258.37)',
    destructive: 'oklch(0.6316 0.2237 11.89)',
    // #1170 Decision 1 (fg-swap AA): keep #f13265, fg #ffffff -> #121212 (4.80:1).
    destructiveForeground: 'oklch(0.1822 0 0)',
    success: 'oklch(0.8211 0.1969 154.06)',
    successForeground: 'oklch(0.1566 0.0438 266.78)',
    warning: 'oklch(0.8989 0.1648 95.59)',
    warningForeground: 'oklch(0.1566 0.0438 266.78)',
  },

  // ── EMERALD ───────────────────────────────────────────────────────────────
  'emerald': {
    ...LIGHT_NEUTRAL,
    primary: 'oklch(0.66 0.1242 167.67)',
    primaryForeground: 'oklch(0.1822 0 0)',
    primaryTint: 'oklch(0.956 0.0277 177.06)',
    accent: 'oklch(0.5644 0.2014 259.39)',
    accentForeground: 'oklch(1 0 0)',
    neutralTint: 'oklch(0.989 0.0068 174.38)',
    // #1170 a11y (keyboard focus): the ring is the accent, not the primary (1.62:1 vs white
    // before). #3071 S2: primary #27aa83 is 2.9:1, so the ring stays the accent, now #1c6ee9 (4.71:1).
    ring: 'oklch(0.5644 0.2014 259.39)',
  },
  'emerald.dark': {
    bg: 'oklch(0.2192 0.0388 165.52)',
    background: 'oklch(0.2192 0.0388 165.52)',
    surface: 'oklch(0.2986 0.0486 162.26)',
    surfaceAlt: 'oklch(0.3509 0.057 162.87)',
    text: 'oklch(0.936 0.0364 163.22)',
    foreground: 'oklch(0.936 0.0364 163.22)',
    textMuted: 'oklch(0.7875 0.073 163.67)',
    heading: 'oklch(1 0 0)',
    subheading: 'oklch(0.936 0.0364 163.22)',
    primary: 'oklch(0.8211 0.1969 154.06)',
    primaryForeground: 'oklch(0.2192 0.0388 165.52)',
    accent: 'oklch(0.6437 0.177 260.68)',
    // #1170 Decision 1 (fg-swap AA): keep blue #4a89f7; fg #ffffff (3.38:1 —
    // fails) -> emerald.dark bg #052016 (5.07:1 — passes AA).
    accentForeground: 'oklch(0.2192 0.0388 165.52)',
    // #1170 Decision 3 (muted-nudge): dark border lifted #3a8a58 -> #509a6c to
    // clear 3:1 vs --muted #1a4432 (3.22:1) while staying >=3:1 vs bg #052016
    // (5.05:1) and surface #133526 (3.95:1). Was #1f533b pre-#1170.
    border: 'oklch(0.6255 0.1024 154.79)',
    input: 'oklch(0.2986 0.0486 162.26)',
    ring: 'oklch(0.8211 0.1969 154.06)',
    muted: 'oklch(0.3509 0.057 162.87)',
    mutedForeground: 'oklch(0.7875 0.073 163.67)',
    destructive: 'oklch(0.6316 0.2237 11.89)',
    // #1170 Decision 1 (fg-swap AA): keep #f13265, fg #ffffff -> #121212 (4.80:1).
    destructiveForeground: 'oklch(0.1822 0 0)',
    success: 'oklch(0.8211 0.1969 154.06)',
    successForeground: 'oklch(0.2192 0.0388 165.52)',
    warning: 'oklch(0.8989 0.1648 95.59)',
    warningForeground: 'oklch(0.2192 0.0388 165.52)',
  },

  // ── SUNSHINE ──────────────────────────────────────────────────────────────
  'sunshine': {
    ...LIGHT_NEUTRAL,
    primary: 'oklch(0.8422 0.1708 85.58)',
    primaryForeground: 'oklch(0.1822 0 0)',
    primaryTint: 'oklch(0.9702 0.0405 91.71)',
    accent: 'oklch(0.2939 0.1395 262.33)',
    accentForeground: 'oklch(1 0 0)',
    neutralTint: 'oklch(0.9914 0.0098 87.47)',
    // #1170 a11y (keyboard focus): the ring is the accent, not the primary (1.35:1 vs white
    // before). #3071 S2: primary #fdc10d still fails 3:1, so the ring is the accent #002270 (14.36:1).
    ring: 'oklch(0.2939 0.1395 262.33)',
  },
  'sunshine.dark': {
    bg: 'oklch(0.2 0.048 301.44)',
    background: 'oklch(0.2 0.048 301.44)',
    surface: 'oklch(0.2419 0.0477 308.13)',
    surfaceAlt: 'oklch(0.2871 0.0574 309.26)',
    text: 'oklch(0.948 0.0335 310.72)',
    foreground: 'oklch(0.948 0.0335 310.72)',
    textMuted: 'oklch(0.7717 0.0704 313.49)',
    heading: 'oklch(1 0 0)',
    subheading: 'oklch(0.948 0.0335 310.72)',
    primary: 'oklch(0.9167 0.1378 95.84)',
    primaryForeground: 'oklch(0.2 0.048 301.44)',
    accent: 'oklch(0.5538 0.056 262.36)',
    accentForeground: 'oklch(1 0 0)',
    // #1170 Decision 3 (muted-nudge): dark border lifted #8a5a9e -> #9566a8 to
    // clear 3:1 vs --muted #332240 (3.29:1) while staying >=3:1 vs bg #1a1028
    // (4.13:1) and surface #261931 (3.75:1). Was #3d2a4f pre-#1170.
    border: 'oklch(0.5845 0.1109 315.8)',
    input: 'oklch(0.2419 0.0477 308.13)',
    ring: 'oklch(0.9167 0.1378 95.84)',
    muted: 'oklch(0.2871 0.0574 309.26)',
    mutedForeground: 'oklch(0.7717 0.0704 313.49)',
    destructive: 'oklch(0.6316 0.2237 11.89)',
    // #1170 Decision 1 (fg-swap AA): keep #f13265, fg #ffffff -> #121212 (4.80:1).
    destructiveForeground: 'oklch(0.1822 0 0)',
    success: 'oklch(0.8211 0.1969 154.06)',
    successForeground: 'oklch(0.2 0.048 301.44)',
    warning: 'oklch(0.9167 0.1378 95.84)',
    warningForeground: 'oklch(0.2 0.048 301.44)',
  },

  // ── MAGENTA ───────────────────────────────────────────────────────────────
  'magenta': {
    ...LIGHT_NEUTRAL,
    primary: 'oklch(0.6007 0.2323 348.1)',
    primaryForeground: 'oklch(0 0 0)',
    accent: 'oklch(0.5644 0.2014 259.39)',
    accentForeground: 'oklch(1 0 0)',
    ring: 'oklch(0.6007 0.2323 348.1)',
  },
  'magenta.dark': {
    bg: 'oklch(0.2 0.048 301.44)',
    background: 'oklch(0.2 0.048 301.44)',
    surface: 'oklch(0.2419 0.0477 308.13)',
    surfaceAlt: 'oklch(0.2871 0.0574 309.26)',
    text: 'oklch(0.948 0.0335 310.72)',
    foreground: 'oklch(0.948 0.0335 310.72)',
    textMuted: 'oklch(0.7717 0.0704 313.49)',
    heading: 'oklch(1 0 0)',
    subheading: 'oklch(0.948 0.0335 310.72)',
    primary: 'oklch(0.693 0.1873 11.98)',
    // #1170 Decision 1 (fg-swap AA): keep #f9607d; fg #ffffff (3.00:1 — fails)
    // -> magenta.dark bg #1a1028 (6.09:1 — passes AA).
    primaryForeground: 'oklch(0.2 0.048 301.44)',
    accent: 'oklch(0.8989 0.1648 95.59)',
    accentForeground: 'oklch(0.2 0.048 301.44)',
    // #1170 Decision 3 (muted-nudge): dark border lifted #8a5a9e -> #9566a8 to
    // clear 3:1 vs --muted #332240 (3.29:1) while staying >=3:1 vs bg #1a1028
    // (4.13:1) and surface #261931 (3.75:1). Shares plum chrome w/ sunshine.dark.
    border: 'oklch(0.5845 0.1109 315.8)',
    input: 'oklch(0.2419 0.0477 308.13)',
    ring: 'oklch(0.693 0.1873 11.98)',
    muted: 'oklch(0.2871 0.0574 309.26)',
    mutedForeground: 'oklch(0.7717 0.0704 313.49)',
    destructive: 'oklch(0.6316 0.2237 11.89)',
    // #1170 Decision 1 (fg-swap AA): keep #f13265, fg #ffffff -> #121212 (4.80:1).
    destructiveForeground: 'oklch(0.1822 0 0)',
    success: 'oklch(0.8211 0.1969 154.06)',
    successForeground: 'oklch(0.2 0.048 301.44)',
    warning: 'oklch(0.8989 0.1648 95.59)',
    warningForeground: 'oklch(0.2 0.048 301.44)',
  },

  // ── MIDNIGHT-BLUEPRINT (standalone; same tokens as storm.dark) ─────────────
  'midnight-blueprint': {
    bg: 'oklch(0.1566 0.0438 266.78)',
    background: 'oklch(0.1566 0.0438 266.78)',
    surface: 'oklch(0.2467 0.053 262.99)',
    surfaceAlt: 'oklch(0.2868 0.0577 261.53)',
    text: 'oklch(0.9253 0.0144 254.61)',
    foreground: 'oklch(0.9253 0.0144 254.61)',
    textMuted: 'oklch(0.7372 0.0343 258.37)',
    heading: 'oklch(1 0 0)',
    subheading: 'oklch(0.9253 0.0144 254.61)',
    primary: 'oklch(0.6437 0.177 260.68)',
    primaryForeground: 'oklch(1 0 0)',
    accent: 'oklch(0.8989 0.1648 95.59)',
    accentForeground: 'oklch(0.1566 0.0438 266.78)',
    // #1170 Decision 3 (muted-nudge): dark border lifted #516ea0 -> #5d79a8 to
    // clear 3:1 vs --muted #1a2a47 (3.25:1) while staying >=3:1 vs bg #050b1f
    // (4.44:1) and surface #13203a (3.68:1). Same navy chrome as storm.dark.
    border: 'oklch(0.5739 0.08 260.49)',
    input: 'oklch(0.2467 0.053 262.99)',
    ring: 'oklch(0.6437 0.177 260.68)',
    muted: 'oklch(0.2868 0.0577 261.53)',
    mutedForeground: 'oklch(0.7372 0.0343 258.37)',
    link: 'oklch(0.6437 0.177 260.68)',
    linkHover: 'oklch(0.7141 0.1396 258.71)',
    destructive: 'oklch(0.6316 0.2237 11.89)',
    // #1170 Decision 1 (fg-swap AA): keep #f13265, fg #ffffff -> #121212 (4.80:1).
    destructiveForeground: 'oklch(0.1822 0 0)',
    success: 'oklch(0.8211 0.1969 154.06)',
    successForeground: 'oklch(0.1566 0.0438 266.78)',
    warning: 'oklch(0.8989 0.1648 95.59)',
    warningForeground: 'oklch(0.1566 0.0438 266.78)',
  },
};

/**
 * Section-level (`[data-theme-section]`) token subset. Only 9 vars per state.
 * Light variants share a neutral chrome; dark variants are nested under `.dark`.
 * Captured verbatim from theme.css section 3.
 */
export interface ThemeSectionTokens {
  bg: string;
  text: string;
  primary: string;
  primaryForeground: string;
  accent: string;
  accentForeground: string;
  border: string;
  muted: string;
  mutedForeground: string;
}

export const SECTION_TOKEN_CSS_VARS: Array<[keyof ThemeSectionTokens, string]> = [
  ['bg', '--bg'],
  ['text', '--text'],
  ['primary', '--primary'],
  ['primaryForeground', '--primary-foreground'],
  ['accent', '--accent'],
  ['accentForeground', '--accent-foreground'],
  ['border', '--border'],
  ['muted', '--muted'],
  ['mutedForeground', '--muted-foreground'],
];

const SECTION_LIGHT_NEUTRAL = {
  bg: 'oklch(1 0 0)',
  text: 'oklch(0.1822 0 0)',
  // #1170 Decision 2 (form-field-only scope): light section --border is
  // decorative (WCAG 1.4.11-exempt) -> REVERTED to original #e4e4e4. Section
  // tokens carry no --input; form fields inside a section inherit the runtime
  // --input (#8e8e8e light) from the enclosing [data-theme] block.
  border: 'oklch(0.9189 0 0)',
  muted: 'oklch(0.958 0.0083 91.48)',
  mutedForeground: 'oklch(0.4276 0 0)',
} as const;

/** order matters — matches theme.css emit order */
export const sectionVariants = [
  'canvas',
  'storm',
  'blue',
  'emerald',
  'sunshine',
  'magenta',
] as const;
export type SectionVariant = (typeof sectionVariants)[number];

export const themeSections: Record<
  SectionVariant,
  { light: ThemeSectionTokens; dark: ThemeSectionTokens }
> = {
  canvas: {
    light: {
      ...SECTION_LIGHT_NEUTRAL,
      primary: 'oklch(0.5644 0.2014 259.39)',
      primaryForeground: 'oklch(1 0 0)',
      accent: 'oklch(0.6007 0.2323 348.1)',
      accentForeground: 'oklch(0 0 0)',
    },
    dark: {
      bg: 'oklch(0.2178 0 0)',
      text: 'oklch(0.9851 0 0)',
      primary: 'oklch(0.6437 0.177 260.68)',
      primaryForeground: 'oklch(1 0 0)',
      accent: 'oklch(0.693 0.1873 11.98)',
      accentForeground: 'oklch(1 0 0)',
      // #1170 Decision 3: section border #404040 -> #7d7d7d (matches canvas.dark runtime)
      border: 'oklch(0.5897 0 0)',
      muted: 'oklch(0.3092 0 0)',
      mutedForeground: 'oklch(0.7668 0 0)',
    },
  },
  storm: {
    light: {
      ...SECTION_LIGHT_NEUTRAL,
      primary: 'oklch(0.2939 0.1395 262.33)',
      primaryForeground: 'oklch(1 0 0)',
      accent: 'oklch(0.6007 0.2323 348.1)',
      accentForeground: 'oklch(0 0 0)',
    },
    dark: {
      bg: 'oklch(0.1566 0.0438 266.78)',
      text: 'oklch(0.9253 0.0144 254.61)',
      primary: 'oklch(0.6437 0.177 260.68)',
      primaryForeground: 'oklch(1 0 0)',
      accent: 'oklch(0.8989 0.1648 95.59)',
      accentForeground: 'oklch(0.1566 0.0438 266.78)',
      // #1170 Decision 3: section border #2a3a57 -> #5d79a8 (matches storm.dark runtime)
      border: 'oklch(0.5739 0.08 260.49)',
      muted: 'oklch(0.2868 0.0577 261.53)',
      mutedForeground: 'oklch(0.7372 0.0343 258.37)',
    },
  },
  blue: {
    light: {
      ...SECTION_LIGHT_NEUTRAL,
      primary: 'oklch(0.5072 0.1845 259.43)',
      primaryForeground: 'oklch(1 0 0)',
      accent: 'oklch(0.8422 0.1708 85.58)',
      accentForeground: 'oklch(0.1822 0 0)',
    },
    dark: {
      bg: 'oklch(0.1566 0.0438 266.78)',
      text: 'oklch(0.9253 0.0144 254.61)',
      primary: 'oklch(0.5551 0.2211 260.57)',
      primaryForeground: 'oklch(1 0 0)',
      accent: 'oklch(0.9167 0.1378 95.84)',
      accentForeground: 'oklch(0.1566 0.0438 266.78)',
      // #1170 Decision 3: section border #2a3a57 -> #5d79a8 (matches blue.dark runtime)
      border: 'oklch(0.5739 0.08 260.49)',
      muted: 'oklch(0.2868 0.0577 261.53)',
      mutedForeground: 'oklch(0.7372 0.0343 258.37)',
    },
  },
  emerald: {
    light: {
      ...SECTION_LIGHT_NEUTRAL,
      primary: 'oklch(0.66 0.1242 167.67)',
      primaryForeground: 'oklch(0.1822 0 0)',
      accent: 'oklch(0.5644 0.2014 259.39)',
      accentForeground: 'oklch(1 0 0)',
    },
    dark: {
      bg: 'oklch(0.2192 0.0388 165.52)',
      text: 'oklch(0.936 0.0364 163.22)',
      primary: 'oklch(0.8211 0.1969 154.06)',
      primaryForeground: 'oklch(0.2192 0.0388 165.52)',
      accent: 'oklch(0.6437 0.177 260.68)',
      accentForeground: 'oklch(1 0 0)',
      // #1170 Decision 3: section border #1f533b -> #509a6c (matches emerald.dark runtime)
      border: 'oklch(0.6255 0.1024 154.79)',
      muted: 'oklch(0.3509 0.057 162.87)',
      mutedForeground: 'oklch(0.7875 0.073 163.67)',
    },
  },
  sunshine: {
    light: {
      ...SECTION_LIGHT_NEUTRAL,
      primary: 'oklch(0.8422 0.1708 85.58)',
      primaryForeground: 'oklch(0.1822 0 0)',
      accent: 'oklch(0.2939 0.1395 262.33)',
      accentForeground: 'oklch(1 0 0)',
    },
    dark: {
      bg: 'oklch(0.2 0.048 301.44)',
      text: 'oklch(0.948 0.0335 310.72)',
      primary: 'oklch(0.9167 0.1378 95.84)',
      primaryForeground: 'oklch(0.2 0.048 301.44)',
      accent: 'oklch(0.5538 0.056 262.36)',
      accentForeground: 'oklch(1 0 0)',
      // #1170 Decision 3: section border #3d2a4f -> #9566a8 (matches sunshine.dark runtime)
      border: 'oklch(0.5845 0.1109 315.8)',
      muted: 'oklch(0.2871 0.0574 309.26)',
      mutedForeground: 'oklch(0.7717 0.0704 313.49)',
    },
  },
  magenta: {
    light: {
      ...SECTION_LIGHT_NEUTRAL,
      primary: 'oklch(0.6007 0.2323 348.1)',
      primaryForeground: 'oklch(0 0 0)',
      accent: 'oklch(0.5644 0.2014 259.39)',
      accentForeground: 'oklch(1 0 0)',
    },
    dark: {
      bg: 'oklch(0.2 0.048 301.44)',
      text: 'oklch(0.948 0.0335 310.72)',
      primary: 'oklch(0.693 0.1873 11.98)',
      primaryForeground: 'oklch(1 0 0)',
      accent: 'oklch(0.8989 0.1648 95.59)',
      accentForeground: 'oklch(0.2 0.048 301.44)',
      // #1170 Decision 3: section border #3d2a4f -> #9566a8 (matches magenta.dark runtime)
      border: 'oklch(0.5845 0.1109 315.8)',
      muted: 'oklch(0.2871 0.0574 309.26)',
      mutedForeground: 'oklch(0.7717 0.0704 313.49)',
    },
  },
};

/**
 * The 9 semantic NAME tokens (--color-*) that map to runtime vars — emitted in
 * the `@theme` block. Base (direct-value) tokens.
 */
export const SEMANTIC_NAME_TOKENS: Array<[string, string]> = [
  ['--color-bg', 'var(--bg)'],
  ['--color-background', 'var(--background)'],
  ['--color-foreground', 'var(--foreground)'],
  ['--color-surface', 'var(--surface)'],
  ['--color-surface-alt', 'var(--surface-alt)'],
  ['--color-text', 'var(--text)'],
  ['--color-text-muted', 'var(--text-muted)'],
  ['--color-heading', 'var(--heading)'],
  ['--color-subheading', 'var(--subheading)'],
  ['--color-primary', 'var(--primary)'],
  ['--color-primary-foreground', 'var(--primary-foreground)'],
  ['--color-accent', 'var(--accent)'],
  ['--color-accent-foreground', 'var(--accent-foreground)'],
  ['--color-border', 'var(--border)'],
  ['--color-input', 'var(--input)'],
  ['--color-ring', 'var(--ring)'],
  ['--color-muted', 'var(--muted)'],
  ['--color-muted-foreground', 'var(--muted-foreground)'],
  ['--color-destructive', 'var(--destructive)'],
  ['--color-destructive-foreground', 'var(--destructive-foreground)'],
  ['--color-success', 'var(--success)'],
  ['--color-success-foreground', 'var(--success-foreground)'],
  ['--color-warning', 'var(--warning)'],
  ['--color-warning-foreground', 'var(--warning-foreground)'],
  ['--color-primary-tint', 'var(--primary-tint)'],
  ['--color-neutral-tint', 'var(--neutral-tint)'],
  ['--color-link', 'var(--link)'],
  ['--color-link-hover', 'var(--link-hover)'],
];

/** The 6 alias NAME tokens (--color-*) emitted in `@theme inline`. */
export const SEMANTIC_ALIAS_NAME_TOKENS: Array<[string, string]> = [
  ['--color-card', 'var(--card)'],
  ['--color-card-foreground', 'var(--card-foreground)'],
  ['--color-popover', 'var(--popover)'],
  ['--color-popover-foreground', 'var(--popover-foreground)'],
  ['--color-secondary', 'var(--secondary)'],
  ['--color-secondary-foreground', 'var(--secondary-foreground)'],
];

/**
 * The static brand color scales registered in `@theme` block 1c (#6756), so
 * their utilities generate (bg-sunshine-500, text-storm-600, …). Each name
 * emits `--color-<name>-<shade>: var(--<name>-<shade>)` for every shade. The
 * raw scale vars live in libs/ui/core/src/styles/tokens.css. They are not
 * runtime-themed. 'blue' is intentionally absent: it would shadow Tailwind's
 * default blue palette.
 */
export const BRAND_SCALE_NAMES = ['sunshine', 'storm', 'magenta', 'emerald-ice'] as const;

/** The shades emitted for each brand scale, in order. */
export const BRAND_SCALE_SHADES = ['000', '100', '200', '300', '400', '500', '600', '700', '800', '900'] as const;
